import {
  FileDownloadError,
  type FileRecord,
  type IFileService,
} from "@/shared/domain/files";

/**
 * The slice of a glTF document the app reads.
 *
 * Deliberately partial: callers read and rewrite the external asset URIs a
 * document names, in `buffers` and `images`, and pass everything else through
 * unchanged. Typing the whole glTF schema would be a maintenance burden that
 * buys nothing.
 */
export interface GltfDocument {
  buffers?: { uri?: string }[];
  images?: { uri?: string }[];
  [key: string]: unknown;
}

/**
 * Downloads a `.gltf` file's document through the file service.
 *
 * Through the file service rather than a `fetch` of its presigned URL, because
 * that is what retries it: the HTTP client underneath tries again on a network
 * error or a transient status from storage, so one S3 hiccup does not fail the
 * whole model.
 *
 * @throws if storage refuses the download, naming the file and the status; if
 * the file is not JSON; or if it is JSON but not an object (a bare array,
 * string, or `null` would otherwise flow onward and fail deep inside Babylon
 * with an unhelpful message)
 */
export async function downloadGltfDocument(
  files: Pick<IFileService, "getJsonFileContents">,
  file: Pick<FileRecord, "file_id" | "relative_path">,
  options?: { abortSignal?: AbortSignal; resourceOwnerId?: string },
): Promise<GltfDocument> {
  let parsed: unknown;
  try {
    parsed = await files.getJsonFileContents<unknown>(file.file_id, options);
  } catch (error) {
    if (error instanceof FileDownloadError) {
      throw new Error(
        `Could not download ${file.relative_path} (${error.status})`,
        { cause: error },
      );
    }
    if (error instanceof SyntaxError) {
      throw new Error("Model file is not valid JSON", { cause: error });
    }
    throw error;
  }

  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    throw new Error("Model file is not a glTF document");
  }
  return parsed as GltfDocument;
}
